import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Modal from "../Shared/Modal";
import Toggle from "../Shared/Toggle";
import PrimaryButton from "../Shared/PrimaryButton";
import SecondaryButton from "../Shared/SecondaryButton";
import { patchAvailability } from "../../api/availabilityApi";
import { useToast } from "../../hooks/useToast";

// Staged "turn on" panel for a type's disabled availabilities. The modal only
// ever opens from the "turned off" state (the strip/warning shows "Turn
// availability on" only when disabled availabilities of that type exist and
// no active one does), so every passed availability starts inactive — the
// meaningful action is choosing which ones to switch ON.
//
// Reachable from both ServiceAttentionStrip and ServiceForm's in-drawer
// warning — both surfaces get the same panel. NOT a form — safe to layer
// over the service drawer (unlike QuickAvailabilityModal; see that file's
// nested-dialog caveat).
//
// The provider flips toggles locally (staged selection, no writes yet), then
// commits the selected rows together via the Cancel / "Turn on" footer. This
// replaces the old fire-and-forget-per-row model, which auto-closed the
// modal as rows disappeared and made it impossible to turn on several
// deliberately.
//
// Props: { open, onClose, kind: "mobile" | "fixed", availabilities: Array }
const ReenableAvailabilityModal = ({
  open,
  onClose,
  kind,
  availabilities = [],
}) => {
  const mobile = kind === "mobile";
  const queryClient = useQueryClient();
  const toast = useToast();

  const [selected, setSelected] = useState(() => new Set());

  // Reset staged selection whenever the modal (re)opens or the kind changes,
  // so a reopened modal never inherits stale selection from a previous visit.
  useEffect(() => {
    setSelected(new Set());
  }, [open, kind]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { mutate: applySelected, isPending: isApplying } = useMutation({
    mutationFn: () => {
      const ids = [...selected];
      return Promise.all(
        ids.map((id) => {
          const formData = new FormData();
          formData.append("isActive", true);
          return patchAvailability({ id, formData });
        })
      );
    },
    onSuccess: () => {
      const count = selected.size;
      toast.success(
        count === 1 ? "Availability on" : "Availabilities on",
        count === 1
          ? "It's back on and taking bookings"
          : "They're back on and taking bookings"
      );
      onClose();
    },
    onError: (err) => {
      const errorMessage =
        err.response?.data?.error?.message ||
        "An error occurred while updating availability status";
      toast.error("Operation Failed", errorMessage);
    },
    // Invalidate on both success AND error: Promise.all rejects on the first
    // failed patch, but earlier patches in the batch may have already succeeded
    // server-side. Refetching here reflects any partial success (the modal
    // stays open on error so the user can retry only the ones still off).
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["availabilities"],
        exact: true,
      });
    },
  });

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mobile ? "Turn on mobile availability" : "Turn on your location"}
      size="md"
      className="z-[60]"
    >
      <ul className="divide-y divide-ink-100">
        {availabilities.map((availability) => (
          <li
            key={availability._id}
            className="flex items-center justify-between gap-x-4 py-3"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
              {availability.name}
            </span>
            <div className="flex-shrink-0">
              {/* srLabel gives the switch an accessible name (its availability)
                  via sr-only text — so it's announced as the availability, not
                  a generic "Toggle setting" — without rendering the visible
                  <label> that Toggle's `label` prop would add (the name is
                  already shown once on the left). */}
              <Toggle
                checked={selected.has(availability._id)}
                disabled={isApplying}
                onChange={() => toggle(availability._id)}
                srLabel={availability.name}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-6 border-t border-ink-100 pt-6 flex justify-end gap-x-4">
        <SecondaryButton colour="gray" onClick={onClose} disabled={isApplying}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          colour="blue"
          onClick={applySelected}
          disabled={selected.size === 0 || isApplying}
        >
          {isApplying
            ? "Turning on…"
            : `Turn on${selected.size >= 1 ? ` (${selected.size})` : ""}`}
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default ReenableAvailabilityModal;
